import {
  DATE_FILTERS,
  callLength,
  callOutcome,
  callsAndJobsBySource,
  formatMoney,
  formatPhone,
  withinWindow,
  type Cents,
  type DateFilter,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchCalls, signVoicemail, type CallRow } from "../../lib/queries";

/**
 * What the phone numbers bought.
 *
 * Jonathon bought a number for the website, one for the yard signs and one for
 * the truck so that this screen could exist: for the money spent on each, how
 * many people rang, how many we missed, and how much work it turned into. The
 * table is one row per source and the arithmetic is all in
 * `callsAndJobsBySource` — nothing here adds anything up (CLAUDE.md rule 3).
 *
 * Underneath it, the calls themselves. That list is not decoration: a missed
 * call from a number nobody has in Roof Board is a lead standing on the
 * doorstep, and this is the only screen that shows one.
 */
export function CallsReport({
  jobs,
  values,
}: {
  jobs: readonly Job[];
  values: ReadonlyMap<string, Cents> | null;
}) {
  const [filter, setFilter] = useState<DateFilter>("month");
  const calls = useAsync(fetchCalls, []);

  const rows = useMemo(() => (calls.data ?? []) as CallRow[], [calls.data]);

  const report = useMemo(
    () =>
      callsAndJobsBySource(rows, jobs, values ?? new Map<string, Cents>(), {
        filter,
      }),
    [rows, jobs, values, filter],
  );

  // Null means the contract dollars have not arrived — or never will, if that
  // fetch failed. Either way the column stays blank rather than printing $0.00
  // and changing its mind, which is the convention every other list follows
  // (see `values` in Shell) and the reason it is null rather than empty.
  const money = (amount: Cents) => (values ? formatMoney(amount) : "");

  // The call list follows the same window as the table above it, off the same
  // function, so that a count and the rows behind it can never disagree.
  const recent = useMemo(
    () => rows.filter((call) => withinWindow(call.started_at, filter)),
    [rows, filter],
  );

  return (
    <div className="list">
      <div className="listhead">
        <h2>Calls by source</h2>
        <div className="lh-right">
          <span className="count">
            {report.total.calls} {report.total.calls === 1 ? "call" : "calls"}
          </span>
          <select
            className="datefilter"
            aria-label="Date range"
            value={filter}
            onChange={(e) => setFilter(e.target.value as DateFilter)}
          >
            {DATE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorNote error={calls.error ?? null} />

      <div className="block" style={{ marginBottom: 16 }}>
        <div className="ewrap">
          <table className="etable" aria-label="Calls by source">
            <thead>
              <tr>
                <th>Source</th>
                <th className="num">Calls</th>
                <th className="num">Missed</th>
                <th className="num">Voicemails</th>
                <th className="num">New callers</th>
                <th className="num">Leads</th>
                <th className="num">Won</th>
                <th className="num">Close</th>
                <th className="num">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="tnone">
                    No calls or leads in this window.
                  </td>
                </tr>
              ) : (
                report.rows.map((row) => (
                  <tr key={row.source}>
                    <td>{row.source}</td>
                    <td className="num">{row.calls}</td>
                    <td className="num">{row.missed}</td>
                    <td className="num">{row.voicemails}</td>
                    <td className="num">{row.newCallers}</td>
                    <td className="num">{row.leads}</td>
                    <td className="num">{row.won}</td>
                    <td className="num">
                      {row.leads > 0
                        ? `${Math.round(row.closeRate * 100)}%`
                        : "—"}
                    </td>
                    <td className="num">{money(row.revenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {report.rows.length > 0 && (
              <tfoot>
                <tr>
                  <td>
                    <b>Total</b>
                  </td>
                  <td className="num">{report.total.calls}</td>
                  <td className="num">{report.total.missed}</td>
                  <td className="num">{report.total.voicemails}</td>
                  <td className="num">{report.total.newCallers}</td>
                  <td className="num">{report.total.leads}</td>
                  <td className="num">{report.total.won}</td>
                  <td className="num">
                    {report.total.leads > 0
                      ? `${Math.round(report.total.closeRate * 100)}%`
                      : "—"}
                  </td>
                  <td className="num">{money(report.total.revenue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="na-hint" style={{ marginTop: 10 }}>
          Calls are counted where they came in; leads and revenue are counted
          where the job says they came from. Outbound calls are not counted as a
          source.
        </div>
      </div>

      <RecentCalls calls={recent} />
    </div>
  );
}

/** The calls themselves, newest first. */
function RecentCalls({ calls }: { calls: readonly CallRow[] }) {
  return (
    <div className="block">
      <h2>
        Calls <span className="na-hint">({calls.length})</span>
      </h2>
      {calls.length === 0 ? (
        <div className="tnone">Nothing here.</div>
      ) : (
        <div className="ewrap">
          <table className="etable" aria-label="Call log">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Source</th>
                <th>What happened</th>
                <th className="num">Length</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {calls.map((call) => (
                <CallLine key={call.call_sid} call={call} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CallLine({ call }: { call: CallRow }) {
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const outcome = callOutcome(call);
  const seconds =
    outcome === "Voicemail" ? call.recording_seconds : call.duration_seconds;

  async function play() {
    if (!call.recording_path) return;
    try {
      setPlaying(await signVoicemail(call.recording_path));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <tr>
      <td>{fmtDateTime(call.started_at)}</td>
      <td>
        {call.job_id ? (
          <a
            href={`#job=${call.job_id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`job=${call.job_id}`);
            }}
          >
            {call.contact?.full_name ?? formatPhone(call.customer_number)}
          </a>
        ) : (
          <>
            {call.contact?.full_name ?? formatPhone(call.customer_number)}
            {!call.contact_id && (
              // The finding this screen exists for: somebody rang a tracking
              // number and Roof Board has never heard of them.
              <span className="na-hint"> · new caller</span>
            )}
          </>
        )}
      </td>
      <td>{call.lead_source ?? "—"}</td>
      <td>{outcome}</td>
      <td className="num">{callLength(seconds)}</td>
      <td>
        {call.recording_path &&
          (playing ? (
            <audio controls autoPlay src={playing} style={{ height: 32 }} />
          ) : (
            <button
              className="addrow ghost"
              // Fire and forget: play() shows its own errors.
              onClick={() => void play()}
            >
              Play
            </button>
          ))}
        {error && <span className="na-hint">{error}</span>}
      </td>
    </tr>
  );
}
